import type { ExtendedUIMessage } from "./chat.types";

/**
 * Turn-identity + normalization primitives for the server-authoritative render path (brief 015).
 *
 * A chat turn carries several keys, each with a distinct job — this module is the one place that
 * owns how they're derived so a reader doesn't have to reconstruct the model from scattered call
 * sites. The compose/slot layer (`ephemeral-slot.ts`) consumes these; it does not redefine them.
 *
 * The keys:
 * - **`id`** — the React/SDK message id. For a persisted `serverHistory` row it IS the `renderKey`;
 *   for a live `useChat` turn it is the `aisdk-`/`uimsg_` stream id.
 * - **`externalMessageId`** — the streamed/persisted stream id. Session-unique (NOT globally), so it
 *   is only safe as the ephemeral-slot drop predicate (`canDropTurnFromSlot`), never as render identity.
 * - **`renderKey`** — globally-unique `base64url([sourceSessionId, externalMessageId])` (B-1445). The
 *   dedupe/React key across the merged root+child tree; a live turn derives its own so it collapses
 *   onto its persisted row by identity. Read via {@link getRenderKey} (falls back to `id`).
 * - **`metadata.streamPartOffset`** — the count of parts this turn inherited from earlier frames of
 *   the same accreting stream (B-1454); {@link sliceLiveTurnToOwnParts} strips them.
 * - (**`toolCallId`**, per part, lives with the part helpers in `chat.helpers.ts`; the slot layer uses
 *   it as the dedupe/twin-drop secondary key.)
 *
 * {@link normalizeLiveTurn} is the canonical "raw SDK turn → render turn" transform (slice + key) that
 * every consumer of the raw `useChat` buffer must go through.
 */

/**
 * Client mirror of the server's `encodeRenderKey` (conversation-history.service.ts):
 * `base64url(JSON.stringify([sourceSessionId, externalMessageId]))`. It MUST stay
 * byte-identical — a live turn's derived renderKey has to equal the renderKey the server
 * put on that turn's persisted row, or the live↔persisted collapse (B-1445) breaks. Node
 * produces this with `Buffer.from(json).toString("base64url")` (no padding, `-`/`_`); the
 * browser path below reproduces it via UTF-8 bytes → base64 → base64url. All inputs here are
 * ASCII (session UUIDs, `aisdk-`/`uimsg_` ids) but the encoder is UTF-8-correct regardless.
 */
export const encodeRenderKey = (sourceSessionId: string, externalMessageId: string): string => {
  const json = JSON.stringify([sourceSessionId, externalMessageId]);
  const bytes = new TextEncoder().encode(json);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

/**
 * The render/React/dedupe identity for a message. Prefers the globally-unique `renderKey`
 * (persisted rows and metadata-tagged live turns) and falls back to `id` for turns with no
 * derivable renderKey — the legacy render path and any un-tagged turn — so behavior is
 * byte-identical wherever `renderKey` is absent.
 */
export const getRenderKey = (message: { id: string; renderKey?: string | null }): string =>
  message.renderKey ?? message.id;

/**
 * Derives a live streamed turn's `renderKey` from the `sourceSessionId` the server tags onto
 * the turn's `start`/`finish` chunks (B-1445 S1) plus the turn's `aisdk-` stream id — which the
 * server persists verbatim as the row's `externalMessageId`, so the derived key equals the
 * persisted row's renderKey by construction. Returns `undefined` (⇒ fall back to bare `id`) when
 * the tag is absent or the turn can't be keyed:
 *
 * - Scoped to `assistant` turns — only streamed assistant frames flow through the tagging site;
 *   user (`uimsg_`) and server-notice (`srvmsg_`) turns never carry it.
 * - Requires a non-empty `sourceSessionId` AND a non-empty `id`. Because the tag rides the `start`
 *   chunk it is present from the turn's first render, so this never flips the key mid-stream.
 */
export const deriveLiveTurnRenderKey = (turn: ExtendedUIMessage): string | undefined => {
  if (turn.role !== "assistant" || !turn.id) {
    return undefined;
  }
  const sourceSessionId = turn.metadata?.sourceSessionId;
  if (typeof sourceSessionId !== "string" || sourceSessionId === "") {
    return undefined;
  }
  return encodeRenderKey(sourceSessionId, turn.id);
};

/**
 * Strips the parts a live turn inherited from earlier frames of the same stream (B-1454).
 *
 * `ai@6` reconstructs every frame of one stream into a single accreting message whose `parts` only
 * ever grow (a new frame's `start` never resets them), so a frame's client turn carries all prior
 * frames' parts as a leading prefix — most visibly on elicit-resume, where the foreground turn
 * inherits the child continuation's already-persisted text and renders it a second time. The server
 * stamps each turn's metadata with `streamPartOffset`: the exact count of those inherited leading
 * parts. We slice them off so the turn contributes only its OWN parts.
 *
 * This is authoritative, not heuristic: the count comes from the server, not from matching content,
 * so a genuinely-new single-frame turn (offset `0`) is never mis-stripped — even if its opening
 * coincides with a prior turn's. Turns with no numeric offset, offset `0`, or an out-of-range offset
 * (e.g. a rare non-clean resume seed) are returned unchanged.
 *
 * **Idempotent:** the sliced output carries `streamPartOffset: 0`, so re-applying (e.g. via a second
 * `normalizeLiveTurn`) is a no-op and can never double-slice already-own parts.
 */
export const sliceLiveTurnToOwnParts = (turn: ExtendedUIMessage): ExtendedUIMessage => {
  const offset = turn.metadata?.streamPartOffset;
  if (typeof offset !== "number" || offset <= 0 || !turn.parts || offset > turn.parts.length) {
    return turn;
  }
  return {
    ...turn,
    parts: turn.parts.slice(offset),
    // Reset the offset on the canonical output: the remaining parts are now entirely this turn's own,
    // so a second slice must inherit nothing.
    metadata: { ...turn.metadata, streamPartOffset: 0 },
  };
};

/**
 * The canonical render representation of a raw `useChat` (SDK) turn: its OWN parts (inherited
 * accretion stripped, B-1454) tagged with its `renderKey` (B-1445). Every consumer that pulls a turn
 * out of the raw accreting buffer for render MUST go through this — both the render-time `liveTurns`
 * and the fast-submit held-turns parking. Normalizing in one place keeps the two representations
 * identical: a held turn that skipped the own-parts slice would keep a prior frame's inherited
 * tool-call id and get dropped by the shared-tool-call dedupe against the persisted parent row (the
 * held elicit turn vanishing during the hold window). Turns with no derivable renderKey keep their
 * bare id; the slice is a no-op for an un-tagged / offset-0 turn.
 */
export const normalizeLiveTurn = (turn: ExtendedUIMessage): ExtendedUIMessage => {
  const ownParts = sliceLiveTurnToOwnParts(turn);
  const renderKey = deriveLiveTurnRenderKey(ownParts);
  return renderKey ? { ...ownParts, renderKey } : ownParts;
};
